import Box from '@mui/material/Box'
import { alpha, type Theme } from '@mui/material/styles'
import React, { useCallback, type KeyboardEvent, type ReactNode } from 'react'

import type { ProductivityColumnMeta } from '../productivityColumns'

export type CellAlign = ProductivityColumnMeta['align']

export const justifyOf = (align: CellAlign): string => {
  if (align === 'right') return 'flex-end'
  if (align === 'center') return 'center'
  return 'flex-start'
}

// Every editable cell fills its track to the same height, so the edit-all grid
// reads as a spreadsheet rather than a row of floating input pills.
export const SHEET_CELL_H = 26

// The grid column owns the width; a track just fills the cell it was rendered
// into, edge to edge with no outer padding.
export const sheetTrackSx = {
  width: '100%',
  minWidth: 0,
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'stretch',
  minHeight: SHEET_CELL_H,
}

export const SheetTrack = ({ children }: { children: ReactNode }) => <Box sx={sheetTrackSx}>{children}</Box>

// A rate or a cost says so, in both of a money cell's states — pinned to the
// cell's left edge so the glyph lines up down the column and the figure stays
// right-aligned against it.
export const DOLLAR_PREFIX = <Box sx={{ mr: 0.5, fontSize: 12, color: 'text.disabled' }}>$</Box>

export const SHEET_CELL_SX = {
  borderRadius: '2px',
  border: '1px solid',
  borderColor: 'transparent',
  transition: 'border-color 120ms, background-color 120ms, box-shadow 120ms',
}

export const FOCUS_RING_SX = {
  borderColor: 'primary.main',
  boxShadow: (theme: Theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.24)}`,
}

// The focus ring's twin, for a cell the grid is refusing to commit.
const ERROR_RING_SX = {
  borderColor: 'error.main',
  boxShadow: (theme: Theme) => `0 0 0 2px ${alpha(theme.palette.error.main, 0.24)}`,
}

// An errored editor: red while merely on screen, ringed red once it has focus.
export const errorCellSx = (message: string) =>
  message ? { borderColor: 'error.main', color: 'error.main', '&:focus': ERROR_RING_SX } : { '&:focus': FOCUS_RING_SX }

// Space and Enter activate a focusable div the way they would a button. Cluster
// controls also stop propagation so the sheet's grid nav doesn't move focus off
// the control that was just pressed.
export const useActivateOnKey = <Element extends HTMLElement>(
  onActivate: (event: KeyboardEvent<Element>) => void,
  stopPropagation = false
) =>
  useCallback(
    (event: KeyboardEvent<Element>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      if (stopPropagation) event.stopPropagation()
      onActivate(event)
    },
    [onActivate, stopPropagation]
  )
